import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Box, Typography } from '@mui/material';
import { useThemeMode } from '../../../theme/ThemeModeContext';
import { CATEGORICAL, CHART_CHROME } from './chartTokens';

export type DonutDatum = { name: string; value: number };

type Props = {
  data: DonutDatum[];
  height?: number;
  centerLabel?: string;
};

// Part-to-whole "at a glance" per the dataviz skill's form guidance — keep
// callers to <= 6 segments (Status Overview: 3, Top Categories: up to 5).
const DonutChart = ({ data, height = 240, centerLabel }: Props) => {
  const { mode } = useThemeMode();
  const colors = CATEGORICAL[mode];
  const chrome = CHART_CHROME[mode];
  const surface = mode === 'light' ? '#fcfcfb' : '#1a1a19';

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: 'grid', placeItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">No data yet</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="62%"
          outerRadius="88%"
          paddingAngle={2}
          stroke={surface}
          strokeWidth={2}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={colors[i % colors.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{ background: surface, border: `1px solid ${chrome.gridline}`, borderRadius: 12, fontSize: 13 }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value) => <span style={{ color: chrome.mutedText, fontSize: 12 }}>{value}</span>}
        />
        {centerLabel && (
          <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fontWeight: 700, fontSize: 15 }}>
            {centerLabel}
          </text>
        )}
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DonutChart;
